import { IndividualAlarmsFilterComponent, IndividualAlarmsFilterOutput } from '../../../core/components/table/filter-panel/filters/individual-alarms-filter/individual-alarms-filter.component';
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { QueryAlarmDataOut, QueryEventAlarmDataIn, PageInfo, SortingType, Client, IndividualAlarmPageQueryDataIn, IndividualAlarmPageDataOut, AlarmType } from '../../../../api/api-reference';
import { IsNewDirective } from '../../../../directives/is-new.directive';
import { ClipboardService } from '../../../../services/clipboard.service';
import { DatepickerComponent } from '../../../core/components/datepicker/datepicker.component';
import { PaginationComponent } from '../../../core/components/pagination/pagination.component';
import { SearchComponent } from '../../../core/components/search/search.component';
import { SortComponent } from '../../../core/components/sort/sort.component';
import { DateRangeInput } from '../../../core/models/date-range-input';
import { getAlertColor } from '../../alert-colors';
import { EventInformationComponent } from '../../raw-alarm-information/raw-alarm-information.component';
import { processDateRange } from '../../../../helper/date-utils';
import { compareAsc } from 'date-fns';

@Component({
  selector: 'app-individual-alarms',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SortComponent,
    SearchComponent,
    DatepickerComponent,
    PaginationComponent,
    IndividualAlarmsFilterComponent,
    EventInformationComponent,
    FormsModule,
    IsNewDirective,
  ],
  templateUrl: './individual-alarms.component.html',
  styleUrl: './individual-alarms.component.scss'
})
export class IndividualAlarmsComponent {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfAlarms: number = 0;
  public lastPageSize: number = 1;
  readonly dayInMs = 24 * 60 * 60 * 1000;
  isDatepickerOpen = false;
  selectedAssetTypeFilters: string[] = [];
  selectedAlarmTypes: AlarmType[] = [];

  

  isModalOpen = false;
  public selectedEvent: QueryAlarmDataOut | undefined;
  getAlertColor = getAlertColor;

  activeTab: string = 'all-events';
  data: IndividualAlarmPageDataOut[] | undefined = [];
  requestData = new IndividualAlarmPageQueryDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    assetTypes: [],
    alarmTypes: [],
  });

  onDataReceived(event: IndividualAlarmsFilterOutput) {
    this.selectedAssetTypeFilters = event.selectedAssetTypes;
    this.selectedAlarmTypes = event.selectedAlarmTypes;
    this.requestData.assetTypes = event.selectedAssetTypes;
    this.requestData.alarmTypes = event.selectedAlarmTypes
    this.reloadTable();
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    if (tab === 'all-events') {
      this.requestData.startDate = undefined;
      this.requestData.endDate = undefined;
    } else if (tab === 'recently-flagged') {
      const startOfDay = new Date();
      startOfDay.setHours(1, 0, 0, 0);

      const endOfDay = new Date();
      endOfDay.setHours(24, 59, 59, 0);

      this.requestData.startDate = startOfDay;
      this.requestData.endDate = endOfDay;
    }
    this.reloadTable(1);
    this.requestData.endDate!.setDate(this.requestData.endDate!.getDate() - 1);
  }

  constructor(
    private toastr: ToastrService,
    private client: Client,
    public clipboardService: ClipboardService,
    private router: Router,
  ) {
    const navState = this.router.lastSuccessfulNavigation?.extras?.state;
    if (navState?.['channelIds']?.length) {
      const alarmTypes: AlarmType[] = navState['channelIds'].map(
        (name: string) => AlarmType[name as keyof typeof AlarmType]
      ).filter(Boolean);
      this.requestData.alarmTypes = alarmTypes;
      this.selectedAlarmTypes = alarmTypes;
    }
    this.reloadTable(1);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.selectedEvent = undefined;
    this.isModalOpen = false;

    this.client.getPage(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfAlarms = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfAlarms / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  handleRowClick(event: string, command: string) {
    if(!event && !command)
    {
      return;
    }
    if (!this.isModalOpen) {
      this.selectedEvent = new QueryAlarmDataOut();
      this.selectedEvent.eventId = event;
      this.selectedEvent.commandId = command;
      this.isModalOpen = true;
    }
  }

  toggleDatepicker() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  toggleModal() {
    this.isModalOpen = !this.isModalOpen;
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  getDuration(startTime?: Date | string, endTime?: Date | string): string {
    if (!startTime || !endTime) return '/';
    const diffMs = new Date(endTime).getTime() - new Date(startTime).getTime();
    if (diffMs <= 0) return '/';
    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  filterDatesInTable() {
    this.client.getPage(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfAlarms = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfAlarms / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
